import React, { useEffect, useState } from "react";
import styles from "../../styles/patient-page-modules/PatientSettings.module.css";
import { FaChevronDown } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";
import {
  getStates,
  getProviders,
  getRpmCodes,
  getCcmCodes,
} from "../../services/case-manager/case-manager";
import Spinner from "../spinner";
import Reload from "../ui/reload-data";
import AddProviderModal from "../patient-detail/patient-modals/addProviderModal";
import MultiSelect from "../ui/multi-select";
import MultiSelectTagInput from "../multiSelectTagInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import Button from "../button-v1";
import clsx from "clsx";
import { getStaff, getNurses } from "../../services/staff";
import PlaceOfServiceSelect from "../place-of-service-select";

const AdditionalInfo = ({
  formData,
  handleChange,
  isOpen,
  toggleSection,
  rpmCodeInfo = [],
  ccmCodeInfo = [],
}) => {
  const accountId = sessionStorage.getItem("acc_id");
  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const patientId = sessionStorage.getItem("_pt_id");
  const [checkedStaff, setCheckedStaff] = useState({});
  const [isAddProviderModalOpen, setIsAddProviderModalOpen] = useState(false);
  const [renderProvider, setRenderProvider] = useState(false);
  const [selectedRpmCodes, setSelectedRpmCodes] = useState({
    rpm_codes: [],
    ccm_codes: [],
  });

  const handleMultiSelectChange = (name, selectedValues) => {
    setSelectedRpmCodes((prev) => ({
      ...prev,
      [name]: selectedValues || [],
    }));
  };

  const {
    data: states,
    isLoading: isLoadingStates,
    error: statesError,
    refetch: refetchStates,
  } = useQuery(["states"], getStates);

  const {
    data: providers,
    isLoading: isLoadingProviders,
    error: providersError,
    refetch: refetchProviders,
  } = useQuery(["providers"], getProviders);

  const {
    data: rpmCodes,
    isLoading: isLoadingRpmCodes,
    error: rpmCodesError,
    refetch: refetchRpmCodes,
  } = useQuery(["rpmCodes"], getRpmCodes);

  const {
    data: ccmCodes,
    isLoading: isLoadingCcmCodes,
    error: ccmCodesError,
    refetch: refetchCcmCodes,
  } = useQuery(["ccmCodes"], () => getCcmCodes(locationId));

  // get staff
  const {
    data: nurseList,
    isLoading: isLoadingStaff,
    error: staffError,
  } = useQuery(["nurse-list"], () => getNurses(true));

  // Provider Modal Functions
  const openAddProviderModal = () => {
    setIsAddProviderModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeAddProviderModal = () => {
    setIsAddProviderModalOpen(false);
    document.body.style.overflow = "auto";
  };

  useEffect(() => {
    if (selectedRpmCodes?.rpm_codes?.length >= 0) {
      handleChange({
        target: { name: "rpm_codes", value: selectedRpmCodes?.rpm_codes },
      });
    }
  }, [selectedRpmCodes.rpm_codes]);

  useEffect(() => {
    if (selectedRpmCodes?.ccm_codes?.length >= 0) {
      handleChange({
        target: { name: "ccm_codes", value: selectedRpmCodes?.ccm_codes },
      });
    }
  }, [selectedRpmCodes.ccm_codes]);

  useEffect(() => {
    if (rpmCodeInfo?.length > 0) {
      setSelectedRpmCodes((prev) => ({
        ...prev,
        rpm_codes: rpmCodeInfo || [],
      }));
    }
  }, [rpmCodeInfo]);

  useEffect(() => {
    if (ccmCodeInfo?.length > 0) {
      // Handling cases where ccmCodeInfo might be strings (codes) or objects
      const formattedCodes = ccmCodeInfo.map(code => {
        // If it's a string (code like "I10"), returning it directly
        if (typeof code === 'string') return code;
        // If it's an object (like from backend), mapping it to ICD code
        return code;
      });

      setSelectedRpmCodes((prev) => ({
        ...prev,
        ccm_codes: formattedCodes || [],
      }));
    }
  }, [ccmCodeInfo]);

  if (
    statesError ||
    providersError ||
    rpmCodesError ||
    ccmCodesError ||
    staffError
  )
    return <Reload message="Could not load data" onReload={refetchStates} />;

  return (
    <div className={`${styles.form_section} ${isOpen ? "" : styles.close}`}>
      <div
        className={styles.section_header}
        onClick={() => toggleSection("sec2")}
      >
        <div className={styles.section_number}>2</div>
        <div>
          <p className={styles.section_heading}>Additional</p>
          <p className={styles.sub_heading}>Additional Patient information</p>
        </div>
        <div className={styles.drop_icon}>
          <FaChevronDown />
        </div>
      </div>

      {isLoadingStates ||
        isLoadingProviders ||
        isLoadingRpmCodes ||
        isLoadingCcmCodes ||
        isLoadingStaff ? (
        <Spinner />
      ) : (
        <div className={styles.input_fields}>
          <div className={styles.input_group}>
            {/* Address Line 1 */}
            <div className={styles.input_wrapper}>
              <label>Address Line 1</label>
              <input
                type="text"
                placeholder="Address Line 1"
                name="address_line_1"
                className={styles.input}
                value={formData.address_line_1}
                onChange={handleChange}
              />
            </div>

            {/* Address Line 2 */}
            <div className={styles.input_wrapper}>
              <label>Address Line 2</label>
              <input
                type="text"
                placeholder="Address Line 2"
                name="address_line_2"
                className={styles.input}
                value={formData.address_line_2}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.input_group}>
            {/* City */}
            <div className={styles.input_wrapper}>
              <label>City</label>
              <input
                type="text"
                placeholder="City"
                name="city"
                className={styles.input}
                value={formData.city}
                onChange={handleChange}
              />
            </div>

            {/* Postal Code */}
            <div className={styles.input_wrapper}>
              <label>Postal Code</label>
              <input
                type="number"
                name="postal_code"
                className={clsx(styles.input, "no-arrows")}
                value={formData.postal_code}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.input_group}>
            {/* State */}
            <div className={styles.input_wrapper}>
              <label>State</label>
              <Select
                onChange={(value) =>
                  handleChange({
                    target: {
                      name: "state",
                      value,
                    },
                  })
                }
                value={formData.state}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select state">
                    {formData.state ?? "Select code state"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {states?.map((state) => (
                    <SelectItem key={state.name} value={state.name}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Provider */}
            <div className={styles.input_wrapper}>
              <label>Provider</label>
              <section className={styles.input_container}>
                <Select
                  onChange={(value) =>
                    handleChange({
                      target: {
                        name: "provider",
                        value,
                      },
                    })
                  }
                  value={formData.provider}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select provider">
                      {(() => {
                        const p = providers?.find(
                          (_p) => _p.id === formData.provider
                        );
                        return p
                          ? `${p.first_name} ${p.last_name}`
                          : "Select provider";
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {/* <SelectItem> + Add New Provider</SelectItem> */}
                    {providers?.map((provider) => (
                      <SelectItem value={provider.id} key={provider.id}>
                        {provider.first_name} {provider.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={() => openAddProviderModal()}>Add</Button>
              </section>
              {/* <select
                name="provider"
                value={formData.provider}
                onChange={(e) => {
                  if (e.target.value === "add_new_provider") {
                    openAddProviderModal();
                  } else {
                    handleChange(e);
                  }
                }}
              >
                <option value="" disabled>
                  Select provider
                </option>
                {providers
                  ?.filter((provider) => provider.active)
                  .map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.first_name} {provider.last_name}
                    </option>
                  ))}
                <option value="add_new_provider" style={{ cursor: "pointer" }}>
                  | + Add new Provider |
                </option>
              </select> */}
            </div>

            {isAddProviderModalOpen && (
              <AddProviderModal
                isOpen={isAddProviderModalOpen}
                onClose={closeAddProviderModal}
                render={renderProvider}
                reRenderer={setRenderProvider}
                providers={providers}
              />
            )}
          </div>

          <div className={styles.input_group}>
            {/* Secondary Provider */}
            <div className={styles.input_wrapper}>
              <label>Secondary Provider</label>
              <input
                type="text"
                placeholder="Secondary Provider"
                name="secondary_provider"
                className={styles.input}
                value={formData.secondary_provider}
                onChange={handleChange}
              />
            </div>
            <div className={styles.input_wrapper}>
              <label>Nurse</label>
              <section className={styles.input_container}>
                <Select
                  onChange={(value) =>
                    handleChange({
                      target: {
                        name: "nurse_id",
                        value,
                      },
                    })
                  }
                  value={formData.provider}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select nurse">
                      {(() => {
                        const p = nurseList?.find(
                          (_p) => _p.id === formData.nurse_id
                        );
                        return p
                          ? p.name
                          : "Select nurse";
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent searchField>
                    {/* <SelectItem> + Add New Provider</SelectItem> */}
                    {nurseList?.map((nurse) => (
                      <SelectItem value={nurse.id} key={nurse.id}>
                        {nurse.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </section>
            </div>
          </div>

          <div className={styles.input_group}>
            {/* Place of Service - only the patient's own location's list */}
            <div className={styles.input_wrapper}>
              <label>Place of Service</label>
              <section className={styles.input_container}>
                <PlaceOfServiceSelect
                  locationId={
                    formData?.location?.id ||
                    sessionStorage.getItem("_pt_loc_id") ||
                    locationId
                  }
                  value={formData?.place_of_service}
                  currentPlace={formData?.place_of_service_detail}
                  onChange={(value) =>
                    handleChange({
                      target: {
                        name: "place_of_service",
                        value,
                      },
                    })
                  }
                />
              </section>
            </div>
            <div className={styles.input_wrapper}></div>
          </div>

          <div className={styles.input_group}>
            {formData?.ccm ? (
              // Full width when the patient isn't also on RPM
              <div
                className={styles.input_wrapper}
                style={formData?.rpm ? undefined : { width: "100%" }}
              >
                <label>CCM Diagnosis Codes</label>
                <MultiSelectTagInput
                  name="ccm_codes"
                  manual={true}
                  options={ccmCodes?.map((code) => ({
                    id: code.icd_code, // Using icd_code as the ID to match RPM behavior
                    name: `${code.name} (${code.icd_code})`,
                  }))}
                  // added short-circuit evaluation in case of null values
                  value={selectedRpmCodes?.ccm_codes || []}
                  onChange={
                    (name, value) => handleMultiSelectChange("ccm_codes", value)
                  }
                  placeholder="Select CCM Diagnosis Codes"
                />
              </div>
            ) : null}

            {formData?.rpm ? (
              // Full width when the patient isn't also on CCM
              <div
                className={styles.input_wrapper}
                style={formData?.ccm ? undefined : { width: "100%" }}
              >
                <label>RPM Diagnosis Codes</label>

                {/* <MultiSelect
                  label="RPM Diagnosis Codes"
                  name="rpm_codes"
                  options={rpmCodes?.map((code) => ({
                    id: code.id,
                    code: code.code,
                    value: code.id,
                    label: `${code.description} (${code.code})`,
                  }))}
                  selected={selectedRpmCodes?.rpm_codes}
                  onChange={(selected) =>
                    handleMultiSelectChange("rpm_codes", selected)
                  }
                  placeholder="Select RPM Diagnosis Codes"
                /> */}

                <MultiSelectTagInput
                  manual={true}
                  name="rpm_codes"
                  // options={rpmCodes.map((code) => ({
                  //   id: code.id,
                  //   name: `${code.description} (${code.code})`,
                  // }))}

                  options={selectedRpmCodes?.rpm_codes?.map((code) => ({
                    id: code,
                    name: code,
                  }))}
                  value={selectedRpmCodes?.rpm_codes}
                  onChange={(name, value) =>
                    handleMultiSelectChange("rpm_codes", value)
                  }
                  placeholder="Select RPM Diagnosis Codes"
                />
              </div>
            ) : null}
          </div>

          {/* <div className={styles.input_group}>
                                      Race
                                      <div className={styles.input_wrapper}>
                                          <label>Race</label>
                                          <input
                                              type="text"
                                              placeholder="Race"
                                              name="race"
                                              className={styles.input}
                                              value={formData.race}
                                              onChange={handleChange}
                                          />
                                      </div>
  
                                      Family Member Name
                                      <div className={styles.input_wrapper}>
                                          <label>Family Member Name</label>
                                          <input
                                              type="text"
                                              placeholder="Family Member Name"
                                              name="family_member_name"
                                              className={styles.input}
                                              value={formData.family_member_name}
                                              onChange={handleChange}
                                          />
                                      </div>
                                  </div> */}

          {/* <div className={styles.input_group}>
                                      Family Member Phone
                                      <div className={styles.input_wrapper}>
                                          <label>Family Member Phone</label>
                                          <input
                                              type="text"
                                              placeholder="(301) 123-4567"
                                              name="family_member_phone"
                                              className={styles.input}
                                              value={formData.family_member_phone}
                                              onChange={handleChange}
                                          />
                                      </div>
  
                                      Family Member Email
                                      <div className={styles.input_wrapper}>
                                          <label>Family Member Email</label>
                                          <input
                                              type="email"
                                              placeholder="Family Member Email"
                                              name="family_member_email"
                                              className={styles.input}
                                              value={formData.family_member_email}
                                              onChange={handleChange}
                                          />
                                      </div>
                                  </div> */}

          {/* <div className={styles.input_group}>
                                      Physician Name
                                      <div className={styles.input_wrapper}>
                                          <label>Physician Name</label>
                                          <input
                                              type="text"
                                              placeholder="Physician Name"
                                              name="physician_name"
                                              className={styles.input}
                                              value={formData.physician_name}
                                              onChange={handleChange}
                                          />
                                      </div>
  
                                      Physician Phone
                                      <div className={styles.input_wrapper}>
                                          <label>Physician Phone</label>
                                          <input
                                              type="text"
                                              placeholder="(301) 123-4567"
                                              name="physician_phone"
                                              className={styles.input}
                                              value={formData.physician_phone}
                                              onChange={handleChange}
                                          />
                                      </div>
                                  </div> */}

          <div className={styles.input_group}>
            {/* Physician Email */}
            {/* <div className={styles.input_wrapper}>
                                          <label>Physician Email</label>
                                          <input
                                              type="email"
                                              placeholder="physician@example.com"
                                              name="physician_email"
                                              className={styles.input}
                                              value={formData.physician_email}
                                              onChange={handleChange}
                                          />
                                      </div> */}

            {/* {formData.rpm && formData.ccm ? (
              <div className={styles.input_wrapper}>
                <label>CCM Diagnosis Codes</label>
                <select
                  name="ccm_codes"
                  value={formData.ccm_codes}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    Select CCM Diagnosis Code
                  </option>
                  {ccmCodes &&
                    ccmCodes.map((code) => (
                      <option key={code.id} value={code.id}>
                        {code.description} ({code.code})
                      </option>
                    ))}
                </select>
              </div>
            ) : (
              <div className={styles.input_wrapper}></div>
            )} */}

            <div className={styles.input_wrapper}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdditionalInfo;